// Shared HTTP client for the calendar bridge (T2). Behaviour must match what the legacy fetch call
// sites in js/calendar.v2.8.js already do: 10s timeout, X-CSRF-Token header from window.csrfToken on
// writes, and a 403 response always means "session expired", checked before the body is read.
// See docs/plans/t2-bridge-and-context-menu.md section 4 for the design this implements.

declare global {
  interface Window {
    csrfToken?: string;
  }
}

export class HttpError extends Error {
  // `serverMessage` is the JSON body's `error` text when the endpoint sent one (undo refusals, workflow denials)
  constructor(public readonly status: number, public readonly serverMessage?: string) {
    super(serverMessage ?? `HTTP ${status}`);
    this.name = "HttpError";
  }
}

export class SessionExpiredError extends Error {
  constructor() {
    super("Session expired");
    this.name = "SessionExpiredError";
  }
}

export class NetworkError extends Error {
  constructor(public readonly cause?: unknown) {
    super("Network error");
    this.name = "NetworkError";
  }
}

export class TimeoutError extends Error {
  constructor() {
    super("Request timed out");
    this.name = "TimeoutError";
  }
}

const DEFAULT_TIMEOUT_MS = 10_000;
const RETRY_DELAY_MS = 2_000;

interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: BodyInit;
  timeoutMs?: number;
  // Lets the request outlive a page navigation (browser caps a keepalive body at 64 KiB)
  keepalive?: boolean;
}

// Best-effort: a non-JSON or empty error body just means no server message.
async function readServerError(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.json()) as { error?: unknown };
    return typeof body.error === "string" && body.error !== "" ? body.error.slice(0, 300) : undefined;
  } catch {
    return undefined;
  }
}

async function fetchOnce(url: string, options: RequestOptions): Promise<Response> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { ...options.headers };
  if (method !== "GET" && window.csrfToken) headers["X-CSRF-Token"] = window.csrfToken;

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  try {
    const response = await fetch(url, { method, headers, body: options.body, signal: controller.signal, keepalive: options.keepalive });
    if (response.status === 403) throw new SessionExpiredError();
    if (!response.ok) throw new HttpError(response.status, await readServerError(response));
    return response;
  } catch (err) {
    if (err instanceof SessionExpiredError || err instanceof HttpError) throw err;
    if (err instanceof DOMException && err.name === "AbortError") throw new TimeoutError();
    throw new NetworkError(err);
  } finally {
    window.clearTimeout(timeout);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

// GET-only retry (D3 in the design doc): a timed-out or network-failed GET is safe to repeat, a
// failed POST/PUT/etc might already have applied — retrying those could duplicate or double-undo.
export async function request(url: string, options: RequestOptions = {}): Promise<Response> {
  const method = options.method ?? "GET";
  try {
    return await fetchOnce(url, options);
  } catch (err) {
    const retryable = method === "GET" && (err instanceof TimeoutError || err instanceof NetworkError);
    if (!retryable) throw err;
    await sleep(RETRY_DELAY_MS);
    return fetchOnce(url, options);
  }
}

export async function getJson<T>(url: string): Promise<T> {
  const response = await request(url, { method: "GET" });
  return (await response.json()) as T;
}

export async function postJson<T>(url: string, body: unknown): Promise<T> {
  const response = await request(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return (await response.json()) as T;
}

export async function postForm<T>(url: string, form: FormData, options: { keepalive?: boolean } = {}): Promise<T> {
  const response = await request(url, { method: "POST", body: form, keepalive: options.keepalive });
  return (await response.json()) as T;
}

// The hidden-form-submit technique from calendar.v2.8.js's postAndNavigate, moved here verbatim:
// a real navigation (not fetch), for endpoints that answer with a redirect (event_delete.php).
export function postAndNavigate(url: string, fields: Record<string, string>): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = url;
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}
